/**
 * KAN-196 (Stripe Ship 1) — the Plan & billing page's state, derived from
 * `get_my_billing_status` (normalized) + the company access level. Pure: no
 * React, no IO. UX only — the server decides what is allowed.
 *
 * States: free · trial (no subscription yet) · trial_subscribed (subscribed,
 * first bill not yet due) · active · change_scheduled (a plan switch is queued)
 * · past_due · cancel_pending (ends at period end) · cancelled · none (unknown),
 * overlaid by the company lock: `state` becomes 'read_only' / 'suspended' while
 * the company is locked, and `base` keeps the underlying billing state so the
 * right action (pay / resubscribe / subscribe) can still be chosen.
 */
const DEAD = new Set(['canceled', 'cancelled', 'incomplete_expired']);
const LIVE = new Set(['active', 'trialing']);

export const BILLING_BASES = Object.freeze([
  'free', 'trial', 'trial_subscribed', 'active', 'change_scheduled', 'past_due', 'cancel_pending', 'cancelled', 'none',
]);

export function deriveBillingBase(status) {
  if (!status) return 'none';
  if (status.billingType === 'free') return 'free';
  const s = status.stripeStatus;
  if (s === 'past_due' || s === 'unpaid' || status.payingStatus === 'past_due') return 'past_due';
  if ((s && DEAD.has(s)) || status.payingStatus === 'cancelled') return 'cancelled';
  if (s && LIVE.has(s)) {
    if (status.cancelAt) return 'cancel_pending';
    if (status.scheduledPlanCode) return 'change_scheduled';
    return s === 'trialing' ? 'trial_subscribed' : 'active';
  }
  if (status.billingType === 'paying') return 'active';
  if (status.billingType === 'trial') return 'trial';
  return 'none';
}

/**
 * @param {object|null} status normalized billing status
 * @param {'full'|'grace'|'read_only'|'suspended'} [level]
 * @returns {{ state: string, base: string, level: string, locked: boolean }}
 */
export function deriveBillingState(status, level = 'full') {
  const base = deriveBillingBase(status);
  const locked = level === 'read_only' || level === 'suspended';
  return { state: locked && base !== 'free' ? level : base, base, level, locked };
}

/**
 * What the person may do from the page. `kind` is the money action; the rest are
 * owner-only plan edits. Owner-only is also enforced on the server.
 * KAN-855: subscribe / resubscribe are OWNER-only. A regular admin gets no primary there, only
 * `ownerOnlyNote: 'subscribe'|'resubscribe'` (the lock row names the verb). Past due keeps Update payment
 * for every admin through the Stripe portal; a company not linked to Stripe has no portal, so a regular
 * admin gets the ask-the-owner note instead (the owner keeps the chooser).
 * @returns {{ primary: 'subscribe'|'resubscribe'|'updatePayment'|'manage'|'resume'|null,
 *   changePlan: boolean, cancel: boolean, undoChange: boolean, ownerOnlyNote: boolean|'subscribe'|'resubscribe' }}
 */
export function billingActions({ base, isOwner = false, isAdmin = true, stripeLinked = false }) {
  const none = { manage: false, primary: null, changePlan: false, cancel: false, undoChange: false, ownerOnlyNote: false };
  if (!isAdmin && !isOwner) return none;
  switch (base) {
    case 'trial':
    case 'none':
      return isOwner ? { ...none, primary: 'subscribe' } : { ...none, ownerOnlyNote: 'subscribe' };
    case 'cancelled':
      return isOwner ? { ...none, primary: 'resubscribe' } : { ...none, ownerOnlyNote: 'resubscribe' };
    case 'past_due':
      return isOwner || stripeLinked ? { ...none, primary: 'updatePayment' } : { ...none, ownerOnlyNote: 'subscribe' };
    case 'cancel_pending':
      // The card can still be updated while the end is pending (owner: Resume + Manage billing).
      return isOwner ? { ...none, primary: 'resume', manage: true } : { ...none, primary: 'manage', ownerOnlyNote: true };
    case 'change_scheduled':
      return { ...none, primary: 'manage', changePlan: isOwner, cancel: isOwner, undoChange: isOwner, ownerOnlyNote: !isOwner };
    case 'trial_subscribed':
    case 'active':
      return { ...none, primary: 'manage', changePlan: isOwner, cancel: isOwner, ownerOnlyNote: !isOwner };
    default:
      return none; // free: never touched
  }
}

/** Stripe says the subscription is live (the return-from-checkout poll stops here). */
export const isSubscriptionLive = (stripeStatus) => stripeStatus === 'active' || stripeStatus === 'trialing';
